import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo } from 'react';
import {
  EuiInMemoryTable,
  EuiLoadingSpinner,
  EuiLink,
} from '@elastic/eui';

import { ComponentTableColumnDefinition } from './Component/ComponentTableColumnDefinition';
import { convertEpochToDate } from '../../../utils';
import { pagination } from './constants';
import { useClusterData } from './ClusterDataProvider';
import { ErrorDisplay } from '../../../components/ErrorDisplay';

const COMPONENT_ID = 'component-details';

export const ComponentDetails = forwardRef((props, ref) => {
  const [clusterDetails, setClusterDetails] = useState({});
  const { stackName, clusterId, showComponentVersionDescribeModal, showComponentDetailsModal } = props;
  const { clusterData, error, isInitialLoading, refetch, isComponentRefreshing } = useClusterData();

  const isLoading = isInitialLoading || isComponentRefreshing(COMPONENT_ID);

  useImperativeHandle(ref, () => ({
    refreshClusterData: async () => {
      if (refetch) {
        try {
          await refetch(COMPONENT_ID);
        } catch (error) {
          console.error('Failed to refresh cluster data:', error);
        }
      }
    }
  }));

  const columns = useMemo(() => [
    {
      ...ComponentTableColumnDefinition[0],
      render: (componentName, component) => (
        <EuiLink onClick={() => showComponentDetailsModal(component)}>
          {componentName}
        </EuiLink>
      ),
    },
    {
      ...ComponentTableColumnDefinition[1],
      render: (major, component) => (
        <EuiLink onClick={() => showComponentVersionDescribeModal(component)}>
          {`${major}.${component.minorVersion}`}
        </EuiLink>
      ),
    },
    ...ComponentTableColumnDefinition.slice(2),
  ], [showComponentDetailsModal, showComponentVersionDescribeModal]);

  const processClusterDescription = (clusterDescription) => {
    if (!clusterDescription) return null;

    const mutableClusterDescription = JSON.parse(JSON.stringify(clusterDescription));
    const { createdBy, creationTime, lastModificationTime } = mutableClusterDescription.auditProperties;
    try {
      const parsedCreator = JSON.parse(createdBy);
      if (typeof parsedCreator !== 'object') throw new Error('Not Object');
      mutableClusterDescription.auditProperties.createdBy = parsedCreator;
    } catch (e) {
      mutableClusterDescription.auditProperties.createdBy = { id: createdBy };
    }

    mutableClusterDescription.auditProperties.creationTime = convertEpochToDate(creationTime);
    mutableClusterDescription.auditProperties.lastModificationTime = convertEpochToDate(lastModificationTime);

    return mutableClusterDescription;
  };

  useEffect(() => {
    if (clusterData) {
      const processed = processClusterDescription(clusterData);
      if (processed) {
        setClusterDetails(processed);
      }
    }
  }, [clusterData]);

  if (error) {
    return <ErrorDisplay />;
  }

  if (isLoading) {
    return <EuiLoadingSpinner size="xl" />;
  }

  if (!clusterDetails?.componentVersionsList) {
    return <EuiLoadingSpinner size="xl" />;
  }

  const { componentVersionsList } = clusterDetails;

  return (
    <EuiInMemoryTable
      pagination={pagination}
      columns={columns}
      items={componentVersionsList}
      sorting={{
        sort: {
          field: 'componentName',
          direction: 'asc',
        },
      }}
    />
  );
});